import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Layout from "../layouts/Layout";
import { API_BASE_URL } from "../layouts/constants";
import {
  ArrowLeft,
  RefreshCw,
  User,
  Calendar,
  Clock,
  Trash2,
  MapPin,
} from "lucide-react";

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("adminToken")
  );
}

function api(path) {
  const base = API_BASE_URL.replace(/\/$/, "");
  if (base.endsWith("/api") && path.startsWith("/api/")) {
    return `${base}${path.replace(/^\/api/, "")}`;
  }
  return `${base}${path}`;
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

export default function PlaceAllocationList() {
  const navigate = useNavigate();
  const { routeId } = useParams();

  const [loading, setLoading] = useState(true);
  const [allocations, setAllocations] = useState([]);
  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  useEffect(() => {
    const token = getToken();
    const isLoggedIn = localStorage.getItem("isLoggedIn");

    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }

    if (!routeId || routeId === "undefined") {
      toast.error("Invalid place id");
      navigate("/route-list");
      return;
    }

    fetchDashboardData(token);
    fetchAllocations();
  }, [routeId]);

  const fetchDashboardData = async (token) => {
    try {
      const res = await fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        setDashboardData(data?.data?.companyDetails || {});
      }
    } catch (error) {
      console.error("Dashboard fetch error:", error);
    }
  };

  const fetchAllocations = async () => {
    try {
      setLoading(true);
      const token = getToken();

      const res = await fetch(api(`/api/placeAllocation/place/${routeId}`), {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to fetch allocations");
      }

      setAllocations(data?.data || []);
    } catch (error) {
      console.error("Fetch place allocations error:", error);
      toast.error(error.message || "Failed to load allocations");
    } finally {
      setLoading(false);
    }
  };

 const handleViewMap = (allocation) => {
  const url = `/sentinel_route_tracking?route_id=${encodeURIComponent(
    allocation.route_id
  )}&user_id=${encodeURIComponent(
    allocation.guard_member_id
  )}&alloc_id=${encodeURIComponent(
    allocation.allocation_id
  )}&type=${encodeURIComponent(allocation.type || "place")}`;

  navigate(url);
};

  const handleDelete = async (allocationId) => {
    if (!window.confirm("Are you sure you want to delete this allocation?")) {
      return;
    }

    try {
      const token = getToken();

      const res = await fetch(api(`/api/placeAllocation/${allocationId}`), {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to delete allocation");
      }

      toast.success("Allocation deleted successfully");
      fetchAllocations();
    } catch (error) {
      console.error("Delete allocation error:", error);
      toast.error(error.message || "Failed to delete allocation");
    }
  };

  const routePage = useMemo(
    () =>
      dashboardData.user_map === "google" ? "/route-master" : "/route-master1",
    [dashboardData.user_map]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--page-bg)] flex flex-col items-center justify-center gap-3">
        <div className="w-9 h-9 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
        <p className="text-sm text-[var(--text-muted)]">
          Loading place allocations...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="route-list"
    >
      <Toaster position="top-right" />

      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-[22px]">
          <div>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="mb-3 inline-flex items-center gap-2 text-[13px] text-[var(--text-muted)] hover:text-[var(--accent)]"
            >
              <ArrowLeft size={16} />
              Back
            </button>

            <h1 className="text-[22px] font-extrabold m-0">
              Place Allocation List
            </h1>
            <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
              View all guards allocated to this place
            </p>
          </div>

          <button
            type="button"
            onClick={fetchAllocations}
            className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5 flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-[18px]">
          <div className="rounded-xl p-4 bg-[var(--btn-bg)] border border-[var(--surface-border)] shadow-[0_4px_18px_var(--accent-glow)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--btn-text)] opacity-75 m-0 mb-1.5">
              Total Allocations
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--btn-text)]">
              {allocations.length}
            </p>
          </div>

          <div className="rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5">
              Place ID
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--accent)]">
              {routeId}
            </p>
          </div>

          <div className="rounded-xl p-4 bg-[var(--surface)] border border-[var(--surface-border)]">
            <p className="text-[10px] font-extrabold uppercase tracking-wider text-[var(--text-faint)] m-0 mb-1.5">
              Type
            </p>
            <p className="text-[30px] font-extrabold leading-none m-0 text-[var(--accent)]">
              Place
            </p>
          </div>
        </div>

        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          <div className="px-6 py-4 border-b border-[var(--divider)]">
            <h3 className="text-[17px] font-extrabold m-0">
              Allocated Guards
            </h3>
            <p className="text-[12px] text-[var(--text-muted)] mt-1">
              Complete allocation list for selected place
            </p>
          </div>

          <div className="overflow-x-auto">
            {allocations.length === 0 ? (
              <div className="text-center py-12 text-[var(--text-muted)] text-[13px]">
                <User size={40} className="opacity-20 mx-auto mb-3" />
                <p>No allocations found for this place</p>
              </div>
            ) : (
              <table className="w-full min-w-[900px] text-[13px]">
                <thead>
                  <tr style={{ background: "var(--btn-bg)" }}>
                    <th className="px-4 py-3 text-left text-[11px] font-extrabold uppercase tracking-wider text-white">
                      #
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-extrabold uppercase tracking-wider text-white">
                      Guard
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-extrabold uppercase tracking-wider text-white">
                      Shift
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-extrabold uppercase tracking-wider text-white">
                      Start Time
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-extrabold uppercase tracking-wider text-white">
                      End Time
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-extrabold uppercase tracking-wider text-white">
                      Phone
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-extrabold uppercase tracking-wider text-white">
                      Type
                    </th>
                    <th className="px-4 py-3 text-left text-[11px] font-extrabold uppercase tracking-wider text-white">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {allocations.map((item, index) => (
                    <tr
                      key={item.allocation_id || index}
                      className={`border-b border-[var(--divider)] hover:bg-[var(--accent-faint)] transition-colors ${
                        index % 2 === 0
                          ? "bg-[var(--card-bg)]"
                          : "bg-[var(--surface)]"
                      }`}
                    >
                      <td className="px-4 py-3 text-[var(--text-muted)]">
                        {index + 1}
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <User size={14} className="text-[var(--accent)]" />
                          <div>
                            <div className="font-extrabold text-[var(--text-primary)]">
                              {item.fld_name || item.fld_username || "Unknown"} (
                              {item.guard_member_id})
                            </div>
                            <div className="text-[11px] text-[var(--text-muted)]">
                              {item.fld_mail || "-"}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-3 text-[var(--text-primary)]">
                        {item.shift_label || "-"}
                      </td>

                      <td className="px-4 py-3 text-[var(--text-muted)]">
                        <div className="flex items-center gap-2">
                          <Calendar size={14} />
                          {formatDateTime(item.start_time)}
                        </div>
                      </td>

                      <td className="px-4 py-3 text-[var(--text-muted)]">
                        <div className="flex items-center gap-2">
                          <Clock size={14} />
                          {formatDateTime(item.end_time)}
                        </div>
                      </td>

                      <td className="px-4 py-3 text-[var(--text-muted)]">
                        {item.fld_phone || "-"}
                      </td>

                      <td className="px-4 py-3">
                        <span className="px-2.5 py-1 rounded-full text-[10px] font-extrabold bg-[rgba(59,130,246,0.12)] border border-[rgba(59,130,246,0.28)] text-[#3b82f6]">
                          {item.type || "place"}
                        </span>
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex gap-2 flex-wrap">
                          <button
                            type="button"
                            onClick={() => handleViewMap(item)}
                            className="p-1.5 rounded-lg border border-[rgba(59,130,246,0.28)] bg-[rgba(59,130,246,0.12)] text-[#3b82f6] hover:bg-[rgba(59,130,246,0.2)] transition-colors"
                            title="View Map"
                          >
                            <MapPin size={15} />
                          </button>

                          <button
                            type="button"
                            onClick={() => handleDelete(item.allocation_id)}
                            className="p-1.5 rounded-lg border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-muted)] hover:bg-[var(--red-faint)] hover:text-[var(--red)] transition-colors"
                            title="Delete Allocation"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}